import React, { useEffect,useState, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import "../i18n";

/**
 * @param {function} onAudioRecorded - callback qui reçoit un Blob audio dès qu'on arrête d'enregistrer
 */
const VoiceCommentRecorder = ({ onAudioRecorded = () => {} }) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimeoutRef = useRef(null);
  const streamRef = useRef(null);

  // ==> START RECORDING
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // <-- on stocke le flux
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Dès que l’enregistreur s’arrête, on fabrique le Blob
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        // On appelle directement la fonction pour envoyer l’audio au parent
        confirmAudioFromBlob(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);

      // Limite à 5 minutes
      recordingTimeoutRef.current = setTimeout(() => {
        stopRecording();
        alert(t("Maximum recording time reached (5 minutes)."));
      }, 5 * 60 * 1000);
    } catch (error) {
      console.error("Erreur d'accès au micro:", error);
      alert(t("Error accessing the microphone."));
    }
  };

  // ==> STOP RECORDING
  //  - On arrête simplement MediaRecorder
  //  - Quand `onstop` est déclenché, on appelle confirmAudioFromBlob(...)
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    // Stopper le flux si existant
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    // Nettoyer le timer
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  
    setIsRecording(false);
  };

  // ==> RESET (pour remettre tout à zéro après l’enregistrement)
  const resetState = () => {
    setIsRecording(false);
    audioChunksRef.current = [];
  };

  // ==> CONFIRM AUDIO
  // Ici on reçoit directement le Blob (pas besoin de fetch l’URL).
  const confirmAudioFromBlob = (audioBlob) => {
    onAudioRecorded(audioBlob);
    resetState();
  };

  // ==> ABANDONNER l'audio (bouton delete)
  // Dans cette version, si l’utilisateur clique “Delete” en cours d’enregistrement,
  // on annule juste l’enregistrement. (Mais attention : si on a déjà stoppé,
  // on a déjà transmis le Blob au parent. Il faut donc gérer la suppression côté parent si besoin.)
  const deleteAudio = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  
    resetState();
  };
  

  useEffect(() => {
    return () => {
      // Nettoyage quand le composant est démonté
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    };
  }, []);
  

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "10px",
        backgroundColor: "#f1f1f1",
        borderRadius: "12px",
        maxWidth: "400px",
        margin: "auto",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        "@media (max-width: 600px)": {
          padding: "5px",
          maxWidth: "100%",
        },
      }}
    >
      {/* BOUTONS START/STOP */}
      <Box sx={{ display: "flex", gap: "20px", marginBottom: "10px" }}>
        {isRecording ? (
          <IconButton
            color="error"
            onClick={stopRecording}
            sx={{ backgroundColor: "#ffebee", ":hover": { backgroundColor: "#ffcdd2" } }}
          >
            <StopIcon fontSize="large" />
          </IconButton>
        ) : (
          <IconButton
            color="primary"
            onClick={startRecording}
            sx={{ backgroundColor: "#e3f2fd", ":hover": { backgroundColor: "#bbdefb" } }}
          >
            <MicIcon fontSize="large" />
          </IconButton>
        )}
      </Box>

      {/* BOUTON DELETE (optionnel) */}
      {/* Si vous voulez permettre d'annuler l'enregistrement (avant l'arrêt),
          vous pouvez proposer ce bouton. */}
      {isRecording && (
        <IconButton onClick={deleteAudio} color="error" size="small">
          <DeleteIcon />
        </IconButton>
      )}

      {/* INDICATION */}
      <Typography
        variant="caption"
        sx={{ marginTop: "10px", color: "#666", textAlign: "center" }}
      >
        {isRecording
          ? t("Recording...")
          : t("Tap the microphone to start recording")}
      </Typography>
    </Box>
  );
};

export default VoiceCommentRecorder;
